import React from 'react'
import { useTranslation } from 'react-i18next'
import { Coins, ShoppingBag, Ticket } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { sdk } from '@/lib/sdk'
import { ROUTES } from '@/lib/routes'
import { useAuth } from '@/lib/AuthContext'
import { useOptionalCommunity } from '@/lib/CommunityContext'
import { useRoute } from '@/lib/RouteContext'
import { AppLink } from '@/components/ui/app-link'

export interface PersonalLink { path: string; icon: LucideIcon; label: string; key: 'tickets' | 'purchases' | 'credits' }

/* One answer per community and person for the whole session: the full menu and the rail are mounted together. */
const creditsAnswers = new Map<string, Promise<boolean>>()

/**
 * Whether this community gives the person credits (F-79): the community has no on/off switch for
 * credits, so the wallet tells it (a balance, a lot, a company wallet or any entry in the statement).
 */
export const useHasCredits = (enabled: boolean): boolean => {
  const community = useOptionalCommunity()
  const { user } = useAuth()
  const key = enabled && community?.id && user?.id ? `${community.id}:${user.id}` : null
  const [has, setHas] = React.useState(false)
  React.useEffect(() => {
    if (!key) { setHas(false); return }
    let alive = true
    let answer = creditsAnswers.get(key)
    if (!answer) {
      answer = Promise.all([sdk.credits.mine(), sdk.credits.myEntries(1, 1)])
        .then(([balance, entries]) => balance.balance !== 0 || balance.lots.length > 0 || Boolean(balance.company) || entries.items.length > 0)
        .catch(() => { creditsAnswers.delete(key); return false })
      creditsAnswers.set(key, answer)
    }
    void answer.then((value) => { if (alive) setHas(value) })
    return () => { alive = false }
  }, [key])
  return has
}

/**
 * "Meus ingressos", "Minhas compras e assinaturas" and "Créditos" (David, 2026-11-05): the person's own things,
 * pinned at the bottom of the menu above Administração. Signed-in people only; Créditos only where
 * the community uses credits (or while its page is open).
 */
export const usePersonalLinks = (): PersonalLink[] => {
  const { t } = useTranslation(['account', 'products'])
  const { isAuthenticated } = useAuth()
  const { currentPath } = useRoute()
  const hasCredits = useHasCredits(isAuthenticated)
  if (!isAuthenticated) return []
  const links: PersonalLink[] = [
    { key: 'tickets', path: ROUTES.myTickets, icon: Ticket, label: t('account:tabs.tickets') },
    { key: 'purchases', path: ROUTES.myPurchases, icon: ShoppingBag, label: t('products:purchases.pageTitle') },
    { key: 'credits', path: ROUTES.myCredits, icon: Coins, label: t('account:tabs.credits') },
  ]
  return links.filter((link) => link.key !== 'credits' || hasCredits || currentPath.startsWith(ROUTES.myCredits))
}

/** The block in the full menu (and the phone drawer), outside the scrolling spaces list. */
export const SidebarPersonalLinks = ({ className }: { className?: string }) => {
  const { t } = useTranslation('navigation')
  const { currentPath } = useRoute()
  const links = usePersonalLinks()
  if (links.length === 0) return null
  return (
    <nav aria-label={t('personalMenu')} className={cn('shrink-0 space-y-1 border-t border-border pt-3 pb-2', className)} data-sidebar-personal>
      {links.map((link) => {
        const active = currentPath.split('?')[0].startsWith(link.path)
        return (
          <AppLink
            key={link.key}
            to={link.path}
            aria-current={active ? 'page' : undefined}
            data-sidebar-personal-item={link.key}
            title={link.label}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition-colors',
              active
                ? 'bg-primary-50 text-primary shadow-[0_4px_12px_rgba(255,101,0,0.12)] backdrop-blur-sm'
                : 'text-foreground-secondary hover:bg-muted'
            )}
          >
            <link.icon className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{link.label}</span>
          </AppLink>
        )
      })}
    </nav>
  )
}
